import type { QueryClient } from '@tanstack/svelte-query';
import { createQuery } from '@tanstack/svelte-query';
import { fromStore, get, toStore, type Readable } from 'svelte/store';
import {
	GetPublishingCartDocument,
	type GetPublishingCartQuery,
	type GetPublishingCartQueryVariables,
} from '../../queries/__generated__/getPublishingCart.generated';
import { getPublishingCartQueryVariables } from '../getPublishingCartQueryVariables/getPublishingCartQueryVariables';
import { FeatureFlags } from '#lib/constants/feature-flags.js';
import { NONEXISTENT_PRODUCT_ID } from '#lib/features/checkout/constants/nonexistent-product-id.js';
import type { CartItems } from '#lib/features/checkout/utils/createCartItemsStore/index.js';
import { getQuery } from '#lib/query-utils/getQuery.js';
import { stringifyIds } from '#lib/websites/nft/utils/stringifyIds/stringifyIds.js';
import { getPublishingCart } from '#lib/websites/publishing/api/getPublishingCart.js';
import { getQueryKeys } from '$global/query-utils/getQueryKeys';
import { capitalizeFirstLetters } from '$global/utils/capitalizeFirstLetters';
import { getImageUrl } from '$global/utils/getImageUrl';

export const getPublishingCartQuery = (
	queryClient: QueryClient,
	cartItems: Readable<CartItems | undefined>
) => {
	const cartItems$ = fromStore(cartItems);

	return createQuery(() => {
		const $cartItems = cartItems$.current;
		const ids = Object.keys($cartItems || {});

		const queryClientData = queryClient.getQueriesData({});

		const existingQueryResult = ids?.length
			? queryClientData?.find(([[key, _, variables]]) => {
					return (
						key === 'getPublishingCart' &&
						ids.every((id) =>
							(
								variables as GetPublishingCartQueryVariables
							)?.filter?.product?.id?.['_in']?.includes(id)
						)
					);
				})
			: null;

		// If there's already cache entry a query fetching more editions than the current one
		if (existingQueryResult?.[1]) {
			return {
				queryKey: getQueryKeys(
					GetPublishingCartDocument,
					getPublishingCartQueryVariables(ids)
				),
				queryFn: () => {
					return {
						Publishing_Books: (
							existingQueryResult?.[1] as GetPublishingCartQuery
						).Publishing_Books?.filter((book) =>
							ids.includes(book.product?.id as string)
						),
					};
				},
			};
		}

		const queryIds = ids?.length ? ids : [NONEXISTENT_PRODUCT_ID];
		const variables = getPublishingCartQueryVariables(queryIds);

		// The query key is the GraphQL one on both paths, so the cache reuse above
		// and the layout's empty-cart seed keep working whichever path is live.
		return FeatureFlags.RestPages['Publishing cart hydration']
			? {
					queryKey: getQueryKeys(GetPublishingCartDocument, variables),
					queryFn: async (): Promise<GetPublishingCartQuery> =>
						stringifyIds(await getPublishingCart(fetch, queryIds)),
				}
			: getQuery(GetPublishingCartDocument, variables);
	});
};

export const formatPublishingItems = (
	$publishingCartQuery: {
		data: GetPublishingCartQuery | undefined;
	},
	cartItems: Readable<CartItems | undefined>
) =>
	($publishingCartQuery.data?.Publishing_Books || []).map((book) => {
		const allDefinedImage = book.featured_image ? book.featured_image : null;

		return {
			image: allDefinedImage
				? {
						width: allDefinedImage.width || 0,
						height: allDefinedImage.height || 0,
						url: `${getImageUrl(allDefinedImage?.filename_disk, '512px')}`,
					}
				: null,
			dropConfigId: null,
			line0: book?.series?.title,
			line1: book?.title,
			line2: (book?.authors?.length
				? book?.authors?.map(
						(author) => author?.Publishing_Authors_And_Artists_id?.full_name
					)
				: book?.artists?.map(
						(artist) => artist?.Publishing_Authors_And_Artists_id?.full_name
					)
			)?.join(', '),
			line3: capitalizeFirstLetters(book?.format),
			productId: book?.product?.id,
			id: book?.id,
			quantity: get(cartItems)?.[book?.product?.id as string]?.quantity || 0,
			currency: book?.product?.currency,
			price: book?.product?.price,
			sku: book?.product?.sku,
			salePrice: book?.product?.sale_price,
			stockLimited: book?.product?.is_stock_limited,
			variant: null,
			groupId: 'Publishing',
			groupName: 'Publishing',
		};
	});

export const createPublishingCartQuery = (
	queryClient: QueryClient,
	cartItems: Readable<CartItems | undefined>,
	defaultLoading?: boolean
) => {
	const publishingCartQuery = getPublishingCartQuery(queryClient, cartItems);

	return toStore(() => {
		return {
			...publishingCartQuery,
			error: publishingCartQuery.error || null,
			data: publishingCartQuery.data || { Merch_Item: [] },
			isLoading: (() => {
				if (!publishingCartQuery.data) {
					return typeof defaultLoading !== 'undefined' ? defaultLoading : false;
				}

				return publishingCartQuery.isPending;
			})(),
			subtotal: Object.values(get(cartItems) || {}).reduce(
				(accumulator, cartItem) => {
					if (!cartItem.quantity || !cartItem.price) {
						return accumulator;
					}

					return (
						accumulator +
						cartItem.quantity * (cartItem.salePrice || cartItem.price)
					);
				},
				0
			),
			formattedItems: formatPublishingItems(publishingCartQuery, cartItems),
		};
	});
};
